import { alpha, createTheme } from '@mui/material/styles';

export function buildMuiThemeWith(colors) {
  return createTheme({
    palette: {
      primary: { main: colors.primary },
      secondary: { main: colors.secondary },
      background: { default: colors.background, paper: colors.white },
      text: { primary: colors.secondary },
      divider: 'rgba(20, 20, 59, 0.08)',
    },
    // Keep default MUI spacing for now; local components use explicit px values
    typography: {
      fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      allVariants: { letterSpacing: 0 },
      h1: { fontWeight: 700, color: colors.primary },
      h4: { color: colors.secondary, fontWeight: 600 },
      h5: { color: colors.secondary, fontWeight: 700 },
      h6: { color: colors.secondary, fontWeight: 700 },
      button: { fontWeight: 600, fontSize: '0.95rem', textTransform: 'none' },
      body1: { fontWeight: 500, fontSize: '1rem' },
      body2: { fontWeight: 500, fontSize: '0.95rem' },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true, // Disable ripple globally to avoid React 18 compatibility issues
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true, // Remove shadows from all buttons
        },
        styleOverrides: {
          root: { 
            borderRadius: '50px', 
            fontWeight: 600, 
            textTransform: 'none', 
            fontSize: '0.95rem',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '6px',
            paddingBottom: '6px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          sizeSmall: {
            fontSize: '0.95rem',
          },
          containedPrimary: { 
            backgroundColor: colors.primary, 
            color: colors.white, 
            boxShadow: 'none',
            '&:hover': { 
              backgroundColor: colors.primary,
              boxShadow: 'none',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: 'rgba(20, 20, 59, 0.08)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              height: 56,
              borderRadius: 100,
              backgroundColor: colors.white,
              '& fieldset': { borderWidth: 1, borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: `${colors.primary}40` },
              '&.Mui-focused fieldset': { borderWidth: 2, borderColor: colors.primary },
            },
            '& .MuiInputLabel-root': { fontWeight: 600, color: colors.secondary },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          transitionDuration: 0, // Disable animations to avoid React 18 compatibility issues
        },
        styleOverrides: {
          paper: { borderRadius: 20, padding: 24 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, alignItems: 'center' },
          standardSuccess: { backgroundColor: `${colors.status.ready}10`, color: colors.status.ready },
          standardWarning: { backgroundColor: `${colors.status.syncing}10`, color: colors.status.syncing },
          standardError: { backgroundColor: `${colors.status.error}10`, color: colors.status.error },
          standardInfo: { backgroundColor: `${colors.primary}10`, color: colors.primary },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: '16px', fontWeight: 700 },
          sizeSmall: { height: 24 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 24,
            boxShadow: 'none',
            borderColor: 'rgba(20, 20, 59, 0.08)',
          }
        }
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: 'rgba(20, 20, 59, 0.08)',
            color: colors.secondary,
          },
          head: {
            color: alpha(colors.secondary, 0.74),
            fontWeight: 800,
            backgroundColor: alpha(colors.secondary, 0.025),
            whiteSpace: 'nowrap',
          },
          body: {
            fontWeight: 600,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': {
              borderBottom: 0,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 3,
            backgroundColor: colors.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: alpha(colors.secondary, 0.68),
            fontWeight: 800,
            textTransform: 'none',
            letterSpacing: 0,
            minHeight: 48,
            '&.Mui-selected': {
              color: colors.primary,
            },
          },
        },
      },
    },
  });
}

export default buildMuiThemeWith;
